import React, { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLayout } from '.';
import { TerminalContext } from '../context';
import WorkerApi from '../worker';
import { Terminal } from '../types';

type TerminalDataProps = PropsWithChildren<{}>;

const reloadInterval = 1 * 60 * 1000;

const TerminalData = ({ children }: TerminalDataProps) => {
    const { t } = useTranslation('common');
    const { terminal, setTerminal } = useContext(TerminalContext);

    const [terminalWithData, setTerminalWithData] = useState<Terminal | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [load, setLoad] = useState<boolean>(true);
    const [reloadTimeoutToken, setReloadTimeoutToken] = useState<null | number>(null);

    const reload = useMemo(() => () => setLoad(true), [setLoad]);

    useEffect(() => {
        if (reloadTimeoutToken !== null) {
            return () => {
                clearTimeout(reloadTimeoutToken);
            };
        }
    }, [reloadTimeoutToken]);

    useEffect(() => {
        setLoad(terminal !== null);
        setTerminalWithData(null);
    }, [terminal]);

    useEffect(() => {
        if (load === true && (terminal?.id ?? null) !== null) {

            let abort = false;
            let id = terminal?.id as Number;

            WorkerApi.terminal({ id }).then((terminal) => {
                if (abort === false) {
                    setTerminalWithData(terminal ?? {} as Terminal);
                    setLoad(false);
                    setReloadTimeoutToken(window.setTimeout(reload, reloadInterval));
                }
            }).catch((e) => {
                if (abort === false) {
                    setError(t('terminal.error.loading'));
                    setLoad(false);
                }
            });

            return (() => {
                abort = true;
            });
        }
    }, [terminal, load, reload, t])

    useEffect(() => {
        if (load === true) {
            setReloadTimeoutToken(null);
        }
    }, [load]);

    if (error !== null) {
        return <ErrorLayout>
            <p>{error}</p>
            <button onClick={() => setLoad(true)}>{t(['action.retry'])}</button>
        </ErrorLayout>
    }

    return <TerminalContext.Provider value={{ terminal: terminalWithData??terminal, setTerminal }}>{children}</TerminalContext.Provider>
}

export default TerminalData;
